/**
 * useInstitutionData Hook
 *
 * Provides institution rating data based on the user's role:
 * - BankUser/BankAdmin: Uses their organization's assigned institution
 * - Other roles: Uses the selected institution from context
 * - Can be overridden with an explicit institution ID
 *
 * Data is fetched from BigQuery API with fallback to static JSON.
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import { useInstitution } from '../../../context/InstitutionContext';
import { useRoleAssumption } from '../../../context/RoleAssumptionContext';
import {
  getLatestRating,
  getIndicatorScores,
  getRatingHistory,
  getRatedInstitutionsWithRatings,
} from '../../../services/institutionService';
import realRatingData from '../../../data/realRatingData.json';
import type { InstitutionRatingData, CAMELComponent, CAMELCode, HistoricalPeriod, SankeyIndicator } from '../types';

// Feature flag: Set to 'false' to use static JSON data instead of BigQuery API
const USE_DYNAMIC_DATA = import.meta.env.VITE_DYNAMIC_INSTITUTIONS !== 'false';

// Default institution for demo purposes
const DEFAULT_INSTITUTION_ID = 'COOP_0190155722001'; // Jardin Azuayo

// Mapping from organization institution names to actual COOP IDs in realRatingData
// This handles the mismatch between org.institutions_access values and realRatingData keys
const INSTITUTION_NAME_TO_ID: Record<string, string> = {
  // Legacy mappings (may still exist in some org configs)
  'JARDIN_AZUAYO': 'COOP_0190155722001',
  'COAC_ATUNTAQUI': 'COOP_1090033456001',
  'COAC_9_DE_OCTUBRE': 'COOP_0590041920001',
  'COAC_CALCETA': 'COOP_1390001920001',
  'COAC_COMERCIO': 'COOP_1390089410001',
  'COAC_CACSPMEC': 'COOP_1390089410001',
  'COAC_TULCAN': 'COOP_1790979016001',
  // Correct mappings based on actual data
  'ANDALUCIA': 'COOP_1790325083001',
  'COOPROGRESO': 'COOP_1790451801001',
  'ATUNTAQUI': 'COOP_1090033456001',
  'CALCETA': 'COOP_1390001920001',
  'COMERCIO': 'COOP_1390089410001',
  'SERVIDORES_PUBLICOS_MEC': 'COOP_1790979016001',
};

// Helper to resolve institution ID from various formats
function resolveInstitutionId(input: string | undefined): string | null {
  if (!input) return null;

  // If it's already a COOP ID, return it
  if (input.startsWith('COOP_')) {
    return input;
  }

  // Try to map from name to ID
  const mappedId = INSTITUTION_NAME_TO_ID[input];
  if (mappedId) {
    return mappedId;
  }

  // Try case-insensitive lookup
  const upperInput = input.toUpperCase();
  for (const [name, id] of Object.entries(INSTITUTION_NAME_TO_ID)) {
    if (name.toUpperCase() === upperInput) {
      return id;
    }
  }

  return null;
}

interface UseInstitutionDataResult {
  /** The institution rating data, or null if not available */
  data: InstitutionRatingData | null;
  /** The institution ID being used */
  institutionId: string | null;
  /** Whether the current user is a bank user/admin (single institution view) */
  isBankUserView: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
}

export function useInstitutionData(explicitInstitutionId?: string): UseInstitutionDataResult {
  const { selectedInstitution } = useInstitution();
  const { effectiveOrganization, effectiveRole } = useRoleAssumption();

  // State for dynamic data fetching
  const [dynamicData, setDynamicData] = useState<InstitutionRatingData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine if this is a bank user view (single institution, no selector)
  const isBankUserView = useMemo(() => {
    return effectiveRole === 'bank_user' || effectiveRole === 'bank_admin';
  }, [effectiveRole]);

  // Determine which institution ID to use
  const institutionId = useMemo(() => {
    // Priority 1: Explicit ID passed as prop
    if (explicitInstitutionId) {
      return resolveInstitutionId(explicitInstitutionId) || explicitInstitutionId;
    }

    // Priority 2: Bank user's assigned institution from their organization
    if (isBankUserView) {
      const assignedInstitution = effectiveOrganization?.institutions_access?.[0];
      const resolvedId = resolveInstitutionId(assignedInstitution);
      return resolvedId || DEFAULT_INSTITUTION_ID;
    }

    // Priority 3: Selected institution from context (for analysts, admins, etc.)
    if (selectedInstitution?.id) {
      return resolveInstitutionId(selectedInstitution.id) || selectedInstitution.id;
    }

    // No institution available
    return null;
  }, [explicitInstitutionId, isBankUserView, effectiveOrganization, selectedInstitution]);

  // Fetch data from API
  const fetchDynamicData = useCallback(async () => {
    if (!institutionId || !USE_DYNAMIC_DATA) return;

    setIsLoading(true);
    setError(null);

    try {
      console.log('[useInstitutionData] Fetching dynamic data for:', institutionId);

      // Fetch latest rating
      const rating = await getLatestRating(institutionId);
      if (!rating) {
        console.log('[useInstitutionData] No rating found for:', institutionId);
        setDynamicData(null);
        setIsLoading(false);
        return;
      }

      // Fetch indicators for the rating period
      const indicators = await getIndicatorScores(institutionId, rating.period);

      // Fetch rating history
      const history = await getRatingHistory(institutionId);

      // Get institution name from the API
      let institutionName = institutionId;
      try {
        const allInstitutions = await getRatedInstitutionsWithRatings();
        const institution = allInstitutions.find(i => i.institution_id === institutionId);
        if (institution) {
          institutionName = institution.institution_name;
        }
      } catch (err) {
        console.warn('[useInstitutionData] Could not fetch institution name:', err);
      }

      // Transform component_scores to CAMEL array
      const camel: CAMELComponent[] = rating.component_scores
        ? Object.entries(rating.component_scores).map(([code, score]) => ({
            code: code as CAMELCode,
            score: score as number,
          }))
        : [];

      // Transform indicators to expected format
      const sankeyIndicators: SankeyIndicator[] = indicators.map(ind => ({
        indicator_id: ind.indicator_id,
        indicator_name: ind.indicator_name,
        indicator_value: String(ind.indicator_value),
        indicator_score: String(ind.indicator_score),
        indicator_category: ind.indicator_category,
      }));

      // Transform history to expected format — sort descending, deduplicate by period
      const seen = new Set<string>();
      const historyData: HistoricalPeriod[] = history
        .sort((a, b) => b.period.localeCompare(a.period))
        .filter(h => {
          if (seen.has(h.period)) return false;
          seen.add(h.period);
          return true;
        })
        .map(h => ({
          date: h.period,
          rating: h.rating_category,
          finalScore: h.numeric_score,
          camel: h.component_scores
            ? Object.entries(h.component_scores).map(([code, score]) => ({
                code: code as CAMELCode,
                score: score as number,
              }))
            : [],
        }));

      const transformedData: InstitutionRatingData = {
        institution_name: institutionName,
        rating: rating.rating_category,
        date: rating.period,
        finalScore: rating.numeric_score,
        camel,
        sankeyIndicators,
        history: historyData,
      };

      console.log('[useInstitutionData] Dynamic data loaded:', {
        institutionId,
        rating: rating.rating_category,
        period: rating.period,
        indicatorCount: indicators.length,
        historyCount: history.length,
      });

      setDynamicData(transformedData);
    } catch (err) {
      console.error('[useInstitutionData] Error fetching dynamic data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  }, [institutionId]);

  // Fetch dynamic data when institutionId changes
  useEffect(() => {
    if (USE_DYNAMIC_DATA && institutionId) {
      fetchDynamicData();
    }
  }, [institutionId, fetchDynamicData]);

  // Get static data as fallback
  const staticData = useMemo(() => {
    if (!institutionId) return null;

    const rawData = (realRatingData as Record<string, unknown>)[institutionId];
    if (!rawData) {
      console.log('[useInstitutionData] No static data found for:', institutionId);
      return null;
    }

    return rawData as InstitutionRatingData;
  }, [institutionId]);

  // Use dynamic data if available, otherwise fall back to static
  const data = USE_DYNAMIC_DATA ? (dynamicData || staticData) : staticData;

  return {
    data,
    institutionId,
    isBankUserView,
    isLoading: USE_DYNAMIC_DATA ? isLoading : false,
    error,
  };
}

export default useInstitutionData;
